import {
  asDeviceId,
  asLocationId,
  asMemberId,
  asOrderId,
  asPractitionerId,
  asScheduleId,
  BookableCandidate,
  ENVELOPE_DURATION_CAP_MINUTES,
} from "@repo/domain";
import { z } from "zod";
import { memberIdSchema } from "./common.schemas";
import { newEntityIdSchema } from "./entity-id.schemas";
import { orderSetIdSchema } from "./order-set.schemas";
import { patientIdSchema } from "./patient.schemas";
import { practitionerIdSchema } from "./practitioner.schemas";
import { parameterAnswerSchema } from "./protocol-definition.schemas";
import { healthcareServiceIdSchema } from "./schedule.schemas";
import { visitIdSchema } from "./visit.schemas";

/**
 * Wire shapes for the booking surface (ADR-0019).
 *
 * A candidate round-trips through an untrusted client, so everything it carries
 * is re-validated here and then handed to the domain constructor — which owns
 * the invariants (start < end, at least one resource seated). Parsing produces
 * a real `BookableCandidate`, not a look-alike object, so a handler cannot be
 * given something that skipped those checks.
 *
 * Nothing clinical rides on the candidate. What is being delivered is named
 * separately by id, and read server-side.
 */
const placementFields = {
  stepId: memberIdSchema.transform(asMemberId).optional(),
  scheduleId: z.string().min(1).transform(asScheduleId),
  start: z.string().datetime(),
  end: z.string().datetime(),
};
const placementSchema = z.discriminatedUnion("resourceType", [
  z.object({
    ...placementFields,
    resourceType: z.literal("Practitioner"),
    resourceId: z.string().min(1).transform(asPractitionerId),
  }),
  z.object({
    ...placementFields,
    resourceType: z.literal("Location"),
    resourceId: z.string().min(1).transform(asLocationId),
  }),
  z.object({
    ...placementFields,
    resourceType: z.literal("Device"),
    resourceId: z.string().min(1).transform(asDeviceId),
  }),
]);

export const bookableCandidateSchema: z.ZodType<BookableCandidate, z.ZodTypeDef, BookableCandidateDto> = z
  .object({
    start: z.string().datetime(),
    end: z.string().datetime(),
    placements: z.array(placementSchema).min(1),
  })
  .transform((candidate) => new BookableCandidate(candidate.start, candidate.end, candidate.placements));

/**
 * The window to search. `end` is optional and defaults to one day out, because
 * a search materializes a proposed Appointment per free start on every calendar
 * involved — a month-wide sweep is tens of thousands of resources for a list
 * nobody scrolls. The UI pages a day at a time.
 */
const instant = z
  .string()
  .datetime({ offset: true })
  .transform((value) => new Date(value).toISOString());

/**
 * Offsets are accepted because a `TZDate` from the calendar serializes with
 * one, and normalized to UTC so nothing downstream compares mixed forms.
 */
const searchWindow = {
  start: instant,
  end: instant.optional(),
};

export const findConsultationCandidatesInputSchema = z.object({
  ...searchWindow,
  healthcareServiceId: healthcareServiceIdSchema,
  practitionerId: practitionerIdSchema,
  modality: z.enum(["in-person", "virtual"]),
});
export type FindConsultationCandidatesInput = z.infer<typeof findConsultationCandidatesInputSchema>;

export const findProtocolVisitCandidatesInputSchema = z.object({
  orderSetId: newEntityIdSchema,
  ...searchWindow,
  protocolUrl: z.string().url(),
  protocolVersion: z.string().min(1),
  patientId: patientIdSchema,
});
export type FindProtocolVisitCandidatesInput = z.infer<typeof findProtocolVisitCandidatesInputSchema>;

export const findCuratedSessionCandidatesInputSchema = z.object({
  ...searchWindow,
  orderSetId: orderSetIdSchema,
});
export type FindCuratedSessionCandidatesInput = z.infer<typeof findCuratedSessionCandidatesInputSchema>;

/**
 * A candidate as it crosses the wire: what a query returns and what a mutation
 * takes back. The server-side type is the domain class; this is its JSON.
 */
export type BookableCandidateDto = {
  start: string;
  end: string;
  placements: readonly z.input<typeof placementSchema>[];
};

/** Availability result enriched with the exact Case selected by server scheduling policy. */
export interface ProtocolVisitCandidateDto {
  candidate: BookableCandidateDto;
  currentCaseId: string;
}

export type FindConsultationCandidatesParams = z.infer<typeof findConsultationCandidatesInputSchema>;
export type FindProtocolVisitCandidatesParams = z.infer<typeof findProtocolVisitCandidatesInputSchema>;
/** Client-facing shape: the wire form, before `orderSetId` is branded by parsing. */
export type FindCuratedSessionCandidatesParams = z.input<typeof findCuratedSessionCandidatesInputSchema>;

export const bookConsultationInputSchema = z.object({
  id: newEntityIdSchema,
  candidate: bookableCandidateSchema,
  patientId: patientIdSchema,
  healthcareServiceId: healthcareServiceIdSchema,
  /**
   * Declared, not inferred. A consult keys off a HealthcareService, which has no
   * encounter class to honour, and the old path guessed from whether a room had
   * been reserved — so a virtual consult that happened to seat a room silently
   * became in-person.
   */
  modality: z.enum(["in-person", "virtual"]),
});
/** What a handler receives: parsed, with a real `BookableCandidate`. */
export type BookConsultationInput = z.infer<typeof bookConsultationInputSchema>;
/**
 * What a CLIENT sends: the wire shape, before the transform.
 *
 * The two differ because parsing rebuilds the candidate through the domain
 * constructor. A caller typed on the parsed shape would have to construct a
 * domain class to call a tRPC procedure, which is exactly backwards.
 */
export type BookConsultationParams = z.input<typeof bookConsultationInputSchema>;

export const bookProtocolVisitInputSchema = z.object({
  examIds: z.record(newEntityIdSchema),
  orderSetId: newEntityIdSchema,
  id: newEntityIdSchema,
  candidate: bookableCandidateSchema,
  currentCaseId: z.string().min(1),
  patientId: patientIdSchema,
  protocolUrl: z.string().url(),
  protocolVersion: z.string().min(1),
});
export type BookProtocolVisitInput = z.infer<typeof bookProtocolVisitInputSchema>;
export type BookProtocolVisitParams = z.input<typeof bookProtocolVisitInputSchema>;

/**
 * The set IS the booking unit, so it is the only thing named. Patient and case
 * are read from it rather than supplied: the old path took a `caseId` from the
 * client and never checked it belonged to the patient whose work was booked.
 */
export const bookCuratedSessionInputSchema = z.object({
  examIds: z.record(newEntityIdSchema),
  id: newEntityIdSchema,
  candidate: bookableCandidateSchema,
  orderSetId: orderSetIdSchema,
});
export type BookCuratedSessionInput = z.infer<typeof bookCuratedSessionInputSchema>;
export type BookCuratedSessionParams = z.input<typeof bookCuratedSessionInputSchema>;

/**
 * A reschedule names only the visit. What work it carries out, which
 * practitioner it belongs to and how long it takes are all facts of the visit
 * already booked — re-supplying them from a client is how a "move this to
 * Tuesday" turns into a differently-shaped visit.
 */
export const findRescheduleCandidatesInputSchema = z.object({
  ...searchWindow,
  visitId: visitIdSchema,
});
export type FindRescheduleCandidatesInput = z.infer<typeof findRescheduleCandidatesInputSchema>;
/** Client-facing shape: the wire form, before `visitId` is branded by parsing. */
export type FindRescheduleCandidatesParams = z.input<typeof findRescheduleCandidatesInputSchema>;

export const rescheduleVisitInputSchema = z.object({
  id: newEntityIdSchema,
  candidate: bookableCandidateSchema,
  visitId: visitIdSchema,
});
export type RescheduleVisitInput = z.infer<typeof rescheduleVisitInputSchema>;
export type RescheduleVisitParams = z.input<typeof rescheduleVisitInputSchema>;

const proposalDuration = z.number().int().positive().multipleOf(5).max(ENVELOPE_DURATION_CAP_MINUTES);
const proposedOrder = { orderId: z.string().min(1).transform(asOrderId) };

/** Scheduling facts only: the visit start and persisted clinical facts are server-owned. */
export const checkOrderSetBookingInputSchema = z
  .object({
    visitId: visitIdSchema,
    proposal: z
      .object({
        envelopeMinutes: proposalDuration,
        steps: z
          .array(
            z
              .object({
                id: memberIdSchema,
                placement: z
                  .object({
                    offsetMinutes: z.number().int().nonnegative().multipleOf(5).max(ENVELOPE_DURATION_CAP_MINUTES),
                    durationMinutes: z.number().int().nonnegative().multipleOf(5).max(ENVELOPE_DURATION_CAP_MINUTES),
                  })
                  .strict(),
                orders: z.array(
                  z.union([
                    z.object(proposedOrder).strict(),
                    z
                      .object({
                        ...proposedOrder,
                        definition: z.object({ url: z.string().url(), version: z.string().min(1) }).strict(),
                        parameterValues: z.array(parameterAnswerSchema),
                      })
                      .strict(),
                  ]),
                ),
              })
              .strict(),
          )
          .min(1),
        /**
         * Held work to withdraw. Beside the steps, not instead of them: every held
         * order stays in the shape, because one the shape omits is deleted.
         */
        revokedOrderIds: z.array(z.string().min(1).transform(asOrderId)).optional(),
      })
      .strict(),
  })
  .strict();
export type CheckOrderSetBookingInput = z.infer<typeof checkOrderSetBookingInputSchema>;
export type CheckOrderSetBookingParams = z.input<typeof checkOrderSetBookingInputSchema>;

export const submitOrderSetBookingInputSchema = checkOrderSetBookingInputSchema;
export type SubmitOrderSetBookingInput = z.infer<typeof submitOrderSetBookingInputSchema>;
export type SubmitOrderSetBookingParams = z.input<typeof submitOrderSetBookingInputSchema>;
